import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Pencil, Trash2, Upload, X, ImageIcon } from "lucide-react";
import RichTextEditor from "@/components/ui/RichTextEditor";
import { resizeImage } from "@/utils/imageResize";

export default function AdminBlogs() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Seuls les fichiers PDF sont acceptés");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Le fichier ne doit pas dépasser 10 Mo");
      return;
    }
    setUploadingPdf(true);
    const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
    const { error } = await supabase.storage.from("blog-pdfs").upload(fileName, file);
    if (error) {
      toast.error("Erreur lors de l'envoi du PDF");
      setUploadingPdf(false);
      return;
    }
    const { data: urlData } = supabase.storage.from("blog-pdfs").getPublicUrl(fileName);
    setFormData((prev) => ({ ...prev, pdf_url: urlData.publicUrl }));
    toast.success("PDF envoyé avec succès");
    setUploadingPdf(false);
    if (pdfInputRef.current) pdfInputRef.current.value = "";
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seuls les fichiers image sont acceptés");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Le fichier ne doit pas dépasser 10 Mo");
      return;
    }
    setUploadingImage(true);
    try {
      const resized = await resizeImage(file, 1200, 1200, 0.85);
      const fileName = `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const { error } = await supabase.storage.from("blog-images").upload(fileName, resized);
      if (error) {
        toast.error("Erreur lors de l'envoi de l'image");
        return;
      }
      const { data: urlData } = supabase.storage.from("blog-images").getPublicUrl(fileName);
      setFormData((prev) => ({ ...prev, image_url: urlData.publicUrl }));
      toast.success("Image envoyée avec succès");
    } catch {
      toast.error("Erreur lors du redimensionnement de l'image");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    image_url: "",
    published: false,
    reading_time: 5,
    author_name: "Agroref",
    pdf_url: "",
    pdf_label: "En savoir plus",
  });

  const { data: blogs, isLoading } = useQuery({
    queryKey: ["admin-blogs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase.from("blog_posts").insert({
        ...data,
        author_id: (await supabase.auth.getUser()).data.user?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      toast.success("Article créé avec succès");
      resetForm();
    },
    onError: () => toast.error("Erreur lors de la création"),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase.from("blog_posts").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      toast.success("Article mis à jour");
      resetForm();
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("blog_posts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-blogs"] });
      toast.success("Article supprimé");
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const resetForm = () => {
    setFormData({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      image_url: "",
      published: false,
      reading_time: 5,
      author_name: "Agroref",
      pdf_url: "",
      pdf_label: "En savoir plus",
    });
    setEditingId(null);
  };

  const handleEdit = (blog: any) => {
    setEditingId(blog.id);
    setFormData({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt || "",
      content: blog.content,
      image_url: blog.image_url || "",
      published: blog.published,
      reading_time: blog.reading_time || 5,
      author_name: blog.author_name || "Agroref",
      pdf_url: blog.pdf_url || "",
      pdf_label: blog.pdf_label || "En savoir plus",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  if (isLoading) return <div className="p-8">Chargement...</div>;

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-bold mb-8">Gestion des Articles de Blog</h1>

      <Card className="p-6 mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Titre</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => {
                const title = e.target.value;
                setFormData({
                  ...formData,
                  title,
                  slug: title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
                });
              }}
              required
            />
          </div>

          <div>
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="author_name">Rédacteur</Label>
              <Input
                id="author_name"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="reading_time">Temps de lecture (minutes)</Label>
              <Input
                id="reading_time"
                type="number"
                min={1}
                value={formData.reading_time}
                onChange={(e) => setFormData({ ...formData, reading_time: parseInt(e.target.value) || 1 })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="excerpt">Extrait</Label>
            <Input
              id="excerpt"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
            />
          </div>

          <div>
            <Label>Contenu</Label>
            <RichTextEditor
              key={editingId || "new"}
              value={formData.content}
              onChange={(val) => setFormData({ ...formData, content: val })}
            />
          </div>

          <div>
            <Label>Image de couverture</Label>
            {formData.image_url ? (
              <div className="flex items-center gap-2 mt-1">
                <img src={formData.image_url} alt="Couverture" className="h-16 w-24 object-cover rounded" />
                <a href={formData.image_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline truncate max-w-[200px]">
                  Image actuelle
                </a>
                <Button type="button" variant="ghost" size="icon" onClick={() => setFormData({ ...formData, image_url: "" })}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="mt-1">
                <input
                  type="file"
                  accept="image/*"
                  ref={imageInputRef}
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="gap-2"
                >
                  <ImageIcon className="h-4 w-4" />
                  {uploadingImage ? "Envoi en cours..." : "Choisir une image"}
                </Button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>PDF (optionnel)</Label>
              {formData.pdf_url ? (
                <div className="flex items-center gap-2 mt-1">
                  <a href={formData.pdf_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary underline truncate max-w-[200px]">
                    PDF actuel
                  </a>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setFormData({ ...formData, pdf_url: "" })}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="mt-1">
                  <input
                    type="file"
                    accept=".pdf"
                    ref={pdfInputRef}
                    className="hidden"
                    onChange={handlePdfUpload}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={uploadingPdf}
                    className="gap-2"
                  >
                    <Upload className="h-4 w-4" />
                    {uploadingPdf ? "Envoi en cours..." : "Choisir un PDF"}
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="pdf_label">Texte du bouton PDF</Label>
              <Input
                id="pdf_label"
                value={formData.pdf_label}
                onChange={(e) => setFormData({ ...formData, pdf_label: e.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="published"
              checked={formData.published}
              onCheckedChange={(checked) => setFormData({ ...formData, published: checked })}
            />
            <Label htmlFor="published">Publié</Label>
          </div>

          <div className="flex gap-2">
            <Button type="submit">{editingId ? "Mettre à jour" : "Créer"}</Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={resetForm}>
                Annuler
              </Button>
            )}
          </div>
        </form>
      </Card>

      <div className="space-y-4">
        {blogs?.map((blog) => (
          <Card key={blog.id} className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-lg">{blog.title}</h3>
                <p className="text-sm text-muted-foreground">{blog.excerpt}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {blog.published ? "✓ Publié" : "✗ Brouillon"} · {(blog as any).reading_time || 5} min · {(blog as any).author_name || "Agroref"}
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleEdit(blog)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteMutation.mutate(blog.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
